import { useEffect, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

/**
 * A dialog that holds a small form — "Make a key", "What the agent does", an
 * update address — with `Confirm`'s manners (bits.tsx): focus moves in, Tab
 * stays in, Esc closes, focus goes back to whatever opened it.
 *
 * Two differences from `Confirm`, both because this holds a form rather than a
 * question: focus starts on the **first field** (there is no safe answer to
 * land on — there is typing to do), and `onClose` may be `null`, for the one
 * moment on this tab when closing must not be possible by accident: while a new
 * key's plaintext is on screen, only "I have stored it" may take it away.
 *
 * It lives with the Connections tab rather than in `ui/kit/`: the kit's rule is
 * that a primitive earns its file when a second screen needs it, and this is
 * the first.
 */
export function Dialog({
  title,
  onClose,
  busy = false,
  wide = false,
  children,
}: {
  title: string;
  /** `null` — Esc does nothing. */
  onClose: (() => void) | null;
  busy?: boolean;
  wide?: boolean;
  children: ReactNode;
}): ReactNode {
  const panel = useRef<HTMLDivElement>(null);
  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = panel.current?.querySelector<HTMLElement>(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])',
    );
    first?.focus();
    return () => {
      if (opener !== null && opener.isConnected) opener.focus();
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      const { busy: inFlight, onClose: close } = latest.current;
      if (!inFlight && close !== null) close();
      return;
    }
    if (e.key !== 'Tab' || panel.current === null) return;
    const focusable = [
      ...panel.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) {
      e.preventDefault();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="modal">
      <div
        ref={panel}
        className={wide ? 'modal__panel conn-dialog conn-dialog--wide' : 'modal__panel conn-dialog'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={onKeyDown}
      >
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  );
}
